import type { Tab, WorkflowFailure } from '../types';

interface SidebarItemData {
  id: string;
  title: string;
  description: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'failed';
  input?: any;
  output?: any;
}

interface WorkflowRun {
  status: string;
  workflowInput?: any;
  workflowOutput?: any;
  workflowFailure?: WorkflowFailure;
  workflowDescription?: string;
  workflowFlowChart?: string;
}

export const generateTabs = (
  item: SidebarItemData,
  workflowRun: WorkflowRun,
  flowContent: React.ReactNode
): Tab[] => {
  const tabs: Tab[] = [];

  // Add input tab first
  const inputContent = item.id === 'workflow' ? workflowRun.workflowInput : item.input;
  tabs.push({
    id: 'input',
    label: 'Input',
    content:
      typeof inputContent === 'object'
        ? JSON.stringify(inputContent, null, 2)
        : String(inputContent || 'No input available'),
    type: 'code',
  });

  // Add output tab second - show failure for failed workflows/tasks, output otherwise
  const isWorkflowItem = item.id === 'workflow';
  const itemStatus = isWorkflowItem ? workflowRun.status : item.status;
  const isFailed = itemStatus === 'failed';
  const isCompleted = itemStatus === 'completed';
  // Use workflow failure for both workflow item and failed tasks (tasks don't have their own failure)
  const failure = workflowRun.workflowFailure;
  const outputContent = isWorkflowItem ? workflowRun.workflowOutput : item.output;

  // Determine output tab status for coloring
  const outputStatus = isFailed ? 'failed' : isCompleted ? 'completed' : undefined;

  if (isFailed && failure) {
    tabs.push({
      id: 'output',
      label: 'Output',
      content: failure,
      type: 'failure',
      status: 'failed',
    });
  } else {
    tabs.push({
      id: 'output',
      label: 'Output',
      content:
        typeof outputContent === 'object'
          ? JSON.stringify(outputContent, null, 2)
          : String(outputContent || 'No output available'),
      type: 'code',
      status: outputStatus,
    });
  }

  tabs.push({
    id: 'flow',
    label: 'Flow',
    content: flowContent,
    type: 'flow',
  });

  tabs.push({
    id: 'details',
    label: 'Description',
    content: {
      description: item.id === 'workflow' ? workflowRun.workflowDescription : item.description,
      flowChart: item.id === 'workflow' ? workflowRun.workflowFlowChart : undefined,
    },
    type: 'details',
  });

  return tabs;
};
